import React, { useState } from 'react';
import { Modal, Button, Badge, Alert, Table } from 'react-bootstrap';
import ButtonGeneral from './buttons/ButtonGeneral';
import BrandDetailModal from './BrandDetailModal';
import useFetch from '../hooks/useFetch';
import { buildEndpoint } from '../app/services/EndPoints';
import { toast } from 'react-toastify';

interface Conflict {
    brand_name: string;
    solicitud: string;
    classes: string;
    reason: string;
}

interface SimilarBrand {
    numero_solicitud: string;
    numero_registro: string;
    clases: string;
    signo_denominacion: string;
    titular: string;
    estado: string;
}

interface FeasibilityResult {
    brand_name: string;
    search_terms: string[];
    search_reasoning: string;
    total_similar_brands: number;
    similar_brands: SimilarBrand[];
    has_conflicts: boolean;
    conflicts: Conflict[];
    recommendations: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
    result: FeasibilityResult | null;
}

const BrandFeasibilityResultModal: React.FC<Props> = ({ show, onHide, result }) => {
    const { doGet } = useFetch();
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedBrandDetail, setSelectedBrandDetail] = useState<any>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    
    if (!result) return null;
    
    // Estilos para las filas clickeables
    const clickableRowStyle: React.CSSProperties = {
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    };
    
    const getConflictColor = () => {
        if (!result.has_conflicts) return 'success';
        return result.conflicts.length > 3 ? 'danger' : 'warning';
    };
    
    const getConflictIcon = () => {
        if (!result.has_conflicts) return 'fa-check-circle';
        return result.conflicts.length > 3 ? 'fa-exclamation-triangle' : 'fa-info-circle';
    };
    
    const handleRowClick = (solicitud: string) => {
        setLoadingDetail(true);
        setShowDetailModal(true);
        
        doGet({
            url: buildEndpoint(`brand-registrations/inapi/brand-detail/${solicitud}`),
            setFetching: setLoadingDetail,
            onSuccess: (response) => {
                setSelectedBrandDetail(response.data);
            },
            onError: (response) => {
                toast.error(response.message || 'Error al obtener detalle de marca');
                setShowDetailModal(false);
            }
        });
    };
    
    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-search me-2"></i>
                    Análisis de Factibilidad con IA
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Header con marca y estado */}
                <div className="text-center mb-4">
                    <h4 className="mb-3">{result.brand_name}</h4>
                    <div className="d-flex justify-content-center align-items-center gap-3">
                        <Badge 
                            bg={getConflictColor()} 
                            className="p-2"
                            style={{ fontSize: '1rem' }}
                        >
                            <i className={`fa ${getConflictIcon()} me-2`}></i>
                            {result.has_conflicts 
                                ? `${result.conflicts.length} Conflicto${result.conflicts.length !== 1 ? 's' : ''} Detectado${result.conflicts.length !== 1 ? 's' : ''}`
                                : 'Sin Conflictos'
                            }
                        </Badge>
                        
                        <Badge bg="info" className="p-2" style={{ fontSize: '1rem' }}>
                            <i className="fa fa-database me-2"></i>
                            {result.total_similar_brands} marcas similares
                        </Badge>
                    </div>
                </div>
                
                {/* Alerta de conflictos */}
                {result.has_conflicts ? (
                    <Alert variant={getConflictColor()} className="mb-4">
                        <Alert.Heading className="h6">
                            <i className="fa fa-exclamation-triangle me-2"></i>
                            Posibles Conflictos Detectados
                        </Alert.Heading>
                        <p className="mb-0">
                            Se encontraron {result.conflicts.length} marcas que podrían generar conflicto.
                            Revisa cada una y evalúa el riesgo según tu criterio profesional.
                        </p>
                    </Alert>
                ) : (
                    <Alert variant="success" className="mb-4">
                        <Alert.Heading className="h6">
                            <i className="fa fa-check-circle me-2"></i>
                            Sin Conflictos Aparentes
                        </Alert.Heading>
                        <p className="mb-0">
                            No se detectaron marcas que puedan generar conflicto directo. 
                            La marca parece tener buena disponibilidad para registro.
                        </p>
                    </Alert>
                )}
                
                {/* Términos de búsqueda */}
                <div className="card mb-4">
                    <div className="card-header bg-light">
                        <h6 className="mb-0">
                            <i className="fa fa-lightbulb me-2 text-warning"></i>
                            Términos de Búsqueda (Generados por IA)
                        </h6>
                    </div>
                    <div className="card-body">
                        <p className="text-muted mb-2">{result.search_reasoning}</p>
                        <div className="d-flex flex-wrap gap-2">
                            {result.search_terms.map((term, index) => (
                                <Badge key={index} bg="secondary" className="p-2">
                                    {term}
                                </Badge>
                            ))}
                        </div>
                    </div>
                </div>
                
                {/* Marcas con conflicto */}
                {result.conflicts.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fa fa-exclamation-circle me-2 text-warning"></i>
                                Marcas que Podrían Generar Conflicto
                            </h6>
                        </div>
                        <div className="card-body p-0">
                            <Table responsive hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Marca</th>
                                        <th>Solicitud</th>
                                        <th>Clases</th>
                                        <th>Razón</th>
                                        <th style={{ width: '50px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.conflicts.map((conflict, index) => (
                                        <tr 
                                            key={index}
                                            style={clickableRowStyle}
                                            onClick={() => handleRowClick(conflict.solicitud)}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                                        >
                                            <td>
                                                <strong>{conflict.brand_name}</strong>
                                            </td>
                                            <td>{conflict.solicitud}</td>
                                            <td>{conflict.classes}</td>
                                            <td className="text-muted small">
                                                {conflict.reason}
                                            </td>
                                            <td className="text-center">
                                                <i className="fa fa-external-link-alt text-primary"></i>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
                
                {/* Todas las marcas similares */}
                {result.similar_brands && result.similar_brands.length > 0 && (
                    <div className="card">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fa fa-database me-2 text-info"></i>
                                Todas las Marcas Similares ({result.total_similar_brands})
                            </h6>
                        </div>
                        <div className="card-body p-0">
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <Table responsive hover className="mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Marca</th>
                                            <th>Solicitud</th>
                                            <th>Titular</th>
                                            <th style={{ width: '50px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.similar_brands.map((brand, index) => (
                                            <tr 
                                                key={index}
                                                style={clickableRowStyle}
                                                onClick={() => handleRowClick(brand.numero_solicitud)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                                            >
                                                <td className="small">{brand.signo_denominacion || 'N/A'}</td>
                                                <td className="small">{brand.numero_solicitud || 'N/A'}</td>
                                                <td className="small text-muted">{brand.titular || 'N/A'}</td>
                                                <td className="text-center">
                                                    <i className="fa fa-external-link-alt text-primary small"></i>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Recomendaciones */}
                <div className="card">
                    <div className="card-header bg-light">
                        <h6 className="mb-0">
                            <i className="fa fa-clipboard-list me-2 text-primary"></i>
                            Recomendaciones Legales
                        </h6>
                    </div>
                    <div className="card-body">
                        <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                            {result.recommendations}
                        </p>
                    </div>
                </div>
            </Modal.Body>
            
            <Modal.Footer>
                <ButtonGeneral
                    type="button"
                    variant="secondary"
                    size="medium"
                    title="Cerrar"
                    onClick={onHide}
                />
            </Modal.Footer>
            
            {/* Modal de detalle de marca */}
            <BrandDetailModal
                show={showDetailModal}
                onHide={() => {
                    setShowDetailModal(false);
                    setSelectedBrandDetail(null);
                }}
                brandDetail={selectedBrandDetail}
                loading={loadingDetail}
            />
        </Modal>
    );
};

export default BrandFeasibilityResultModal;

